import { z } from 'zod';
import { api } from './api';

/** How much to delete.
 *
 * `OLDER_THAN` and `LAST_DAYS` are opposites — one keeps the recent window,
 * the other keeps everything before it. They are separate modes rather than
 * one "days" number so the meaning can never be inferred wrongly.
 */
export const RANGE_MODES = ['ALL', 'OLDER_THAN', 'LAST_DAYS', 'CUSTOM'] as const;
export type RangeMode = (typeof RANGE_MODES)[number];

export interface RangeInput {
  mode: RangeMode;
  days?: number | null;
  date_from?: string | null;
  date_to?: string | null;
}

export const CONFIRMATION_PHRASE = 'УДАЛИТЬ';

const resolvedRange = {
  mode: z.string(),
  date_from: z.string().nullable().optional().default(null),
  date_to: z.string().nullable().optional().default(null),
};

export const draftPreviewSchema = z.object({
  ...resolvedRange,
  total: z.number(),
  imports: z.number(),
  orphan_files: z.number(),
  breakdown: z.record(z.number()).default({}),
});

export const auditPurgePreviewSchema = z.object({
  ...resolvedRange,
  total: z.number(),
  breakdown: z.record(z.number()).default({}),
});

export const securityEventSchema = z.object({
  id: z.string(),
  actor_user_id: z.string(),
  actor_email: z.string(),
  action: z.string(),
  deleted_count: z.number(),
  date_from: z.string().nullable(),
  date_to: z.string().nullable(),
  filter_mode: z.string(),
  performed_at: z.string(),
  details: z.record(z.unknown()).nullable().optional(),
});

export type DraftPreview = z.infer<typeof draftPreviewSchema>;
export type AuditPurgePreview = z.infer<typeof auditPurgePreviewSchema>;
export type SecurityEvent = z.infer<typeof securityEventSchema>;

export const adminService = {
  previewDrafts: (range: RangeInput): Promise<DraftPreview> =>
    api.post('/admin/drafts/preview-delete', draftPreviewSchema, range),

  deleteDrafts: (range: RangeInput, confirmation: string): Promise<DraftPreview> =>
    api.post('/admin/drafts/delete', draftPreviewSchema, { ...range, confirmation }),

  previewAuditPurge: (range: RangeInput): Promise<AuditPurgePreview> =>
    api.post('/admin/audit/preview-purge', auditPurgePreviewSchema, range),

  purgeAudit: (range: RangeInput, confirmation: string): Promise<AuditPurgePreview> =>
    api.post('/admin/audit/purge', auditPurgePreviewSchema, { ...range, confirmation }),

  securityEvents: (signal?: AbortSignal): Promise<SecurityEvent[]> =>
    api.get('/admin/security-events', z.array(securityEventSchema), signal),
};
